import { ThunkDispatch } from "core-integration/src/store";
import { getElements } from "core-integration/src/store/elements/reducers";
import { showElementSummary } from "core-integration/src/store/elements/thunks";
import { getTeams } from "core-integration/src/store/teams/reducers";
import { Badge } from "plos/src/components/Badge";
import { UnstyledRACButton } from "plos/src/components/buttons/UnstyledRACButton";
import { externalLinkIcon } from "plos/src/components/links/links.css";
import PageTitle from "plos/src/components/PageTitle";
import Subheading from "plos/src/components/Subheading";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { WebViewContext } from "plos/src/contexts/WebViewContext";
import ExternalIcon from "plos/src/img/icons/external.svg?react";
import { contentMain, rightSidebarLayout } from "plos/src/layouts";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  Disclosure,
  DisclosureGroup,
  DisclosurePanel,
} from "react-aria-components";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useTrackingContext } from "../../contexts/TrackingContext";
import ChevronDown from "../../img/icons/chevron-down.svg?react";
import Info from "../../img/icons/info.svg?react";
import { HelmetHead } from "../HelmetHead";
import ScoutNav from "../scout/ScoutNav";
import { reduceElementsBySetPieceTakers, reduceNotesByTeamId } from "./helpers";
import {
  badgeWrap,
  chevronDown,
  disclosureHeading,
  elementStyles,
  explainedSection,
  noteItem,
  noteLink,
  noteList,
  setPieceTakersStyles,
  setPieceTakersWrapper,
  setPieceTitle,
  teamContentInner,
  teamDetails,
  teamName,
} from "./setPieceTakers.css";
import { INote, ISPTElement, ISPTTeam, IStateNotes } from "./types";

const SetPieceTakers = () => {
  const { t } = useTranslation("scout");
  const dispatch = useDispatch<ThunkDispatch>();
  const teams = useSelector(getTeams);
  const elements = useSelector(getElements);
  const [notes, setNotes] = useState({} as IStateNotes);
  const setPieceTakersByTeamId = useMemo(
    () => reduceElementsBySetPieceTakers(elements, teams),
    [elements, teams],
  );
  const teamsArr: ISPTTeam[] = Object.keys(setPieceTakersByTeamId)
    .map((key: string) => setPieceTakersByTeamId[key])
    .sort((a, b) => a.team.name.localeCompare(b.team.name));

  const { isWebView } = useContext(WebViewContext);
  const { firePageViewEvent } = useTrackingContext();

  useEffect(() => {
    firePageViewEvent("fantasy scout", "set piece takers");
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch("/api/team/set-piece-notes/");
        const { last_updated, teams } = await res.json();
        setNotes({ last_updated, byId: reduceNotesByTeamId(teams) });
      } catch (error) {
        // continue regardless of error
      }
    }
    fetchData();
  }, []);

  const renderElements = (setPieceTypes: ISPTElement[]) => {
    if (setPieceTypes.length === 0) {
      return (
        <div className={elementStyles}>
          {t("setPieceTakers.noSetPieceTakers", "No Set-Piece Takers")}
        </div>
      );
    }

    return setPieceTypes
      .sort((a: ISPTElement, b: ISPTElement) => a.order - b.order)
      .map((element: ISPTElement) => (
        <div className={elementStyles} data-testid="element" key={element.id}>
          <Info onClick={() => dispatch(showElementSummary(element.id))} />
          <span>{element.name}</span>
        </div>
      ));
  };

  return (
    <div className={rightSidebarLayout}>
      <div className={contentMain}>
        {!isWebView && (
          <>
            <PageTitle title={t("scout", "The Scout") as string} />
            <ScoutNav />
          </>
        )}
        <HelmetHead
          title={t(
            "setPieceTakers.metadata.title",
            "Set-Piece Takers, Player Statistics | Fantasy Premier League",
          )}
          description={t(
            "setPieceTakers.metadata.description",
            "View each Premier League team's set-piece takers for penalties, free kicks and corners. For more information, visit the official website of the Premier League.",
          )}
        />
        <section className={setPieceTakersStyles}>
          <DisclosureGroup
            className={setPieceTakersWrapper}
            allowsMultipleExpanded
          >
            {teamsArr.map((team: ISPTTeam) => {
              const teamNotes = notes.byId?.[team.team.id];
              return (
                <Disclosure key={team.team.id} data-testid="team">
                  <SurfaceContainer>
                    <UnstyledRACButton
                      className={disclosureHeading}
                      slot="trigger"
                    >
                      <div className={teamDetails}>
                        <span className={badgeWrap}>
                          <Badge team={team.team} isPresentation />
                        </span>
                        <h3 className={teamName}>{team.team.name}</h3>
                      </div>
                      <ChevronDown className={chevronDown} />
                    </UnstyledRACButton>
                    <DisclosurePanel>
                      <div className={teamContentInner}>
                        <div>
                          <h4 className={setPieceTitle}>
                            {t("setPieceTakers.penalties", "Penalties")}
                          </h4>
                          {renderElements(team.penalties)}
                        </div>
                        <div>
                          <h4 className={setPieceTitle}>
                            {t(
                              "setPieceTakers.directFreeKicks",
                              "Direct free-kicks",
                            )}
                          </h4>
                          {renderElements(team.direct_freekicks)}
                        </div>
                        <div>
                          <h4 className={setPieceTitle}>
                            {t(
                              "setPieceTakers.cornersAndIndirectFreeKicks",
                              "Corners & indirect free-kicks",
                            )}
                          </h4>
                          {renderElements(team.corners_and_indirect_freekicks)}
                        </div>
                      </div>
                      {notes.byId && teamNotes && (
                        <ul
                          data-testid={`team-${team.team.id}-notes`}
                          className={noteList}
                        >
                          {teamNotes.notes.length > 0 &&
                            teamNotes.notes.map(
                              (note: INote, index: number) => (
                                <li
                                  className={noteItem}
                                  key={`note-team-${team.team.id}-${index}`}
                                  data-testid={`team-${team.team.id}-note`}
                                >
                                  {note.info_message ? (
                                    note.source_link ? (
                                      <a
                                        href={note.source_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={noteLink}
                                      >
                                        <span>
                                          {note.info_message}
                                          {note.external_link && (
                                            <ExternalIcon
                                              className={externalLinkIcon}
                                            />
                                          )}
                                        </span>
                                      </a>
                                    ) : (
                                      <span>{note.info_message}</span>
                                    )
                                  ) : null}
                                </li>
                              ),
                            )}
                        </ul>
                      )}
                    </DisclosurePanel>
                  </SurfaceContainer>
                </Disclosure>
              );
            })}
          </DisclosureGroup>
          <SurfaceContainer>
            <div className={explainedSection}>
              <Subheading>
                <Trans ns="scout" i18nKey="setPieceTakers.explainer.heading">
                  Set-Piece Takers Explained
                </Trans>
              </Subheading>
              <p>
                <Trans ns="scout" i18nKey="setPieceTakers.explainer.p1">
                  This table displays a list of expected set-piece takers for
                  each Premier League club. It’s based on a mix of information
                  gathered from last season’s matches, pre-season friendlies
                  and, where appropriate, a player’s role at a previous club and
                  predicted role at their new club. As the season progresses,
                  the table is constantly reviewed and updated as new
                  information becomes available on assigned set-piece takers.
                </Trans>
              </p>
            </div>
          </SurfaceContainer>
        </section>
      </div>
    </div>
  );
};

export default SetPieceTakers;
